import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { 
  CalendarDays, Plus, Search, Filter, Check, X, 
  Clock, AlertCircle, CheckCircle, XCircle, Calendar 
} from 'lucide-react';
import { timeManagementAPI, hcmAPI } from '../../../services/api';
import { toast } from 'sonner';

const Absences = () => {
  const navigate = useNavigate();
  const [absences, setAbsences] = useState([]);
  const [absenceTypes, setAbsenceTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [absencesRes, typesRes, employeesRes] = await Promise.all([
        timeManagementAPI.getAbsences(),
        timeManagementAPI.getAbsenceTypes(),
        hcmAPI.getEmployees()
      ]);
      setAbsences(absencesRes.data || []);
      setAbsenceTypes(typesRes.data || []);
      setEmployees(employeesRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load absences');
    } finally {
      setLoading(false);
    }
  };

  const filteredAbsences = absences.filter(absence => {
    const matchesSearch = 
      (absence.employee_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (absence.employee_id || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || absence.approval_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleApprove = async (absenceId) => {
    try {
      await timeManagementAPI.updateAbsence(absenceId, { 
        approval_status: 'Approved',
        approved_by: 'ADMIN' // In real app, get from auth context
      });
      toast.success('Absence approved');
      loadData();
    } catch (error) {
      toast.error('Failed to approve absence');
    }
  };

  const handleReject = async (absenceId) => {
    try {
      await timeManagementAPI.updateAbsence(absenceId, { 
        approval_status: 'Rejected',
        approved_by: 'ADMIN'
      });
      toast.success('Absence rejected');
      loadData();
    } catch (error) {
      toast.error('Failed to reject absence');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      'Pending': { color: 'bg-yellow-100 text-yellow-700', icon: AlertCircle },
      'Approved': { color: 'bg-green-100 text-green-700', icon: CheckCircle },
      'Rejected': { color: 'bg-red-100 text-red-700', icon: XCircle },
      'Cancelled': { color: 'bg-gray-100 text-gray-700', icon: XCircle },
    };
    const { color, icon: Icon } = config[status] || config['Pending'];
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${color}`}>
        <Icon className="h-3 w-3" />
        {status}
      </span>
    );
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="absences-page">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm')}>HCM</span>
            <span>/</span>
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm/time')}>Time Management</span>
            <span>/</span>
            <span>Absences</span>
          </div>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Absence Records</h1>
              <p className="text-gray-600 mt-1">Manage employee absence requests and leave records (IT2001)</p>
            </div>
            <Button onClick={() => setShowForm(true)} data-testid="btn-create-absence">
              <Plus className="h-4 w-4 mr-2" />
              Record Absence
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertCircle className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Pending</p>
                <p className="text-xl font-semibold text-yellow-600">
                  {absences.filter(a => a.approval_status === 'Pending').length}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Approved</p>
                <p className="text-xl font-semibold text-green-600">
                  {absences.filter(a => a.approval_status === 'Approved').length}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Rejected</p>
                <p className="text-xl font-semibold text-red-600">
                  {absences.filter(a => a.approval_status === 'Rejected').length}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <CalendarDays className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Records</p>
                <p className="text-xl font-semibold">{absences.length}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input 
              placeholder="Search by employee..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">All Statuses</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>

        {/* Table */}
        <Card className="overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : filteredAbsences.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No absence records found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Employee</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Type</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Period</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Days</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Reason</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredAbsences.map((absence) => (
                    <tr key={absence.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{absence.employee_name || absence.employee_id}</p>
                          <p className="text-sm text-gray-500">{absence.employee_id}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {absence.absence_type_name || absence.absence_type_id}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          {absence.start_date} - {absence.end_date}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {absence.absence_days || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate">
                        {absence.reason || '-'}
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(absence.approval_status)}
                      </td>
                      <td className="px-4 py-3">
                        {absence.approval_status === 'Pending' && (
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-green-600 hover:bg-green-50"
                              onClick={() => handleApprove(absence.id)}
                              data-testid={`approve-${absence.id}`}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleReject(absence.id)}
                              data-testid={`reject-${absence.id}`}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Create Form Modal */}
        {showForm && (
          <AbsenceFormModal 
            absenceTypes={absenceTypes}
            employees={employees}
            onClose={() => setShowForm(false)}
            onSave={() => { setShowForm(false); loadData(); }}
          />
        )}
      </div>
    </Layout>
  );
};

// Absence Form Modal
const AbsenceFormModal = ({ absenceTypes, employees, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    employee_id: '',
    absence_type_id: '',
    start_date: '',
    end_date: '',
    reason: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [calculatedDays, setCalculatedDays] = useState(null);
  const [calculationLoading, setCalculationLoading] = useState(false);
  const [calculationDetails, setCalculationDetails] = useState(null);

  // Calculate days when employee, type, or dates change
  useEffect(() => {
    const calculateDays = async () => {
      if (formData.employee_id && formData.absence_type_id && formData.start_date && formData.end_date) {
        setCalculationLoading(true);
        try {
          const response = await timeManagementAPI.calculateAbsenceDays({
            employee_id: formData.employee_id,
            absence_type_id: formData.absence_type_id,
            start_date: formData.start_date,
            end_date: formData.end_date
          });
          setCalculatedDays(response.data.working_days);
          setCalculationDetails(response.data);
        } catch (error) {
          console.error('Failed to calculate days:', error);
          setCalculatedDays(null);
          setCalculationDetails(null);
        } finally {
          setCalculationLoading(false);
        }
      } else {
        setCalculatedDays(null);
        setCalculationDetails(null);
      }
    };

    const debounceTimer = setTimeout(calculateDays, 300);
    return () => clearTimeout(debounceTimer);
  }, [formData.employee_id, formData.absence_type_id, formData.start_date, formData.end_date]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.employee_id || !formData.absence_type_id || !formData.start_date || !formData.end_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await timeManagementAPI.createAbsence(formData);
      toast.success('Absence recorded successfully');
      onSave();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const firstError = detail[0];
        const fieldName = firstError?.loc?.slice(-1)[0] || 'field';
        toast.error(`${fieldName}: ${firstError?.msg || 'Validation error'}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to create absence');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold">Record Absence</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label>
            <select
              value={formData.employee_id}
              onChange={(e) => setFormData({...formData, employee_id: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              required
            >
              <option value="">Select Employee</option>
              {employees.map(emp => (
                <option key={emp.employee_id} value={emp.employee_id}>
                  {emp.personal_data?.first_name} {emp.personal_data?.last_name} ({emp.employee_id})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Absence Type *</label>
            <select
              value={formData.absence_type_id}
              onChange={(e) => setFormData({...formData, absence_type_id: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              required
            >
              <option value="">Select Type</option>
              {absenceTypes.filter(t => t.status === 'Active').map(type => (
                <option key={type.absence_type_id} value={type.absence_type_id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
              <Input 
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
              <Input 
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                required
              />
            </div>
          </div>

          {/* Calculated Days Display */}
          {(calculatedDays !== null || calculationLoading) && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-orange-800">Calculated Days:</span>
                {calculationLoading ? (
                  <span className="text-sm text-orange-600">Calculating...</span>
                ) : (
                  <span className="text-lg font-bold text-orange-900">{calculatedDays} days</span>
                )}
              </div>
              {calculationDetails && !calculationLoading && (
                <div className="text-xs text-orange-700 space-y-1">
                  <div className="flex justify-between">
                    <span>Calendar Days:</span>
                    <span>{calculationDetails.calendar_days}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Weekend Days (excluded):</span>
                    <span>{calculationDetails.weekend_days}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Holidays (excluded):</span>
                    <span>{calculationDetails.holidays_in_period}</span>
                  </div>
                  {calculationDetails.holiday_calendar_name && (
                    <div className="flex justify-between">
                      <span>Holiday Calendar:</span>
                      <span className="truncate ml-2">{calculationDetails.holiday_calendar_name}</span>
                    </div>
                  )}
                  {calculationDetails.work_schedule_name && (
                    <div className="flex justify-between">
                      <span>Work Schedule:</span>
                      <span className="truncate ml-2">{calculationDetails.work_schedule_name}</span>
                    </div>
                  )}
                  {calculationDetails.personnel_area_name && !calculationDetails.work_schedule_name && (
                    <div className="flex justify-between">
                      <span>Personnel Area:</span>
                      <span className="truncate ml-2">{calculationDetails.personnel_area_name}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-orange-200 pt-1 mt-1">
                    <span className="font-medium">Calendar Source:</span>
                    <span className="font-medium">{calculationDetails.calendar_source || 'Default'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Counting Rule:</span>
                    <span className="font-medium">{calculationDetails.counting_rule}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Warning for Non-Working Days */}
          {calculationDetails && calculationDetails.has_non_working_days && calculationDetails.warnings && calculationDetails.warnings.length > 0 && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800 text-sm">Warning: Selected dates include non-working days</p>
                  <ul className="text-xs text-amber-700 mt-2 space-y-1">
                    {calculationDetails.warnings.map((warning, idx) => (
                      <li key={idx}>• {warning}</li>
                    ))}
                  </ul>
                  <p className="text-xs text-amber-600 mt-2 italic">
                    Consider changing the dates to exclude weekends and holidays.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
            <Input 
              value={formData.reason}
              onChange={(e) => setFormData({...formData, reason: e.target.value})}
              placeholder="Reason for absence"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              rows={3}
              placeholder="Additional notes"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Absences;
